import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, IconButton, List, ListItem, ListItemText } from '@mui/material';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { HelpPageListItem } from '../models/helpPageListItem';
import { HelpPageStatusEnum } from '../models/helpPageStatus.enum';
import { getHelpPagesInCategoryOrder, saveHelpPageOrder } from '../services/helpPageService';
import { HelpPageStatusChip } from './helpPageStatusChip';

type HelpPageOrderDialogProps = {
  open: boolean;
  categoryId: number;
  categoryName: string;
  /** saved är true när ordningen sparades, så att sidan kan ladda om listan. */
  onClose: (saved: boolean) => void;
};

// Samma ordning som hjälpcentret: platsen, och titeln (svensk ordning) när två artiklar har samma plats.
const titleOrder = new Intl.Collator('sv', { sensitivity: 'accent' });
const byPlaceThenTitle = (a: HelpPageListItem, b: HelpPageListItem) => a.sortOrder - b.sortOrder || titleOrder.compare(a.title, b.title);

export const HelpPageOrderDialog = (props: HelpPageOrderDialogProps) => {
  const { open, categoryId, categoryName, onClose } = props;
  const { t } = useTranslation(['helpArticles', 'common']);

  const [articles, setArticles] = useState<HelpPageListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  // Hämtas på nytt varje gång dialogen öppnas, så att listan stämmer med kategorin just nu. Listan töms först, annars
  // syns (och kan sparas) en ordning som avbröts förra gången om hämtningen misslyckas.
  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const load = async () => {
      setArticles([]);
      setLoading(true);
      try {
        const result = await getHelpPagesInCategoryOrder(categoryId);
        if (!cancelled) setArticles([...result.data.results].sort(byPlaceThenTitle));
      } catch (e) {
        ConsoleHelper.log(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [open, categoryId]);

  const move = (index: number, direction: -1 | 1) => {
    setArticles((prev) => {
      const next = [...prev];
      [next[index], next[index + direction]] = [next[index + direction], next[index]];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const pageIds = articles.map((a) => a.id);
      await saveHelpPageOrder(categoryId, pageIds);
      toast.success(t('helpArticles:categories.orderSaved') as string);
      onClose(true);
    } catch (e) {
      // Felet (t.ex. att artiklarna i kategorin har ändrats) visas av interceptorn. Dialogen står kvar.
      ConsoleHelper.log(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <GenericDialog
      open={open}
      // Krysset och Esc gör inget medan ordningen sparas, som Avbryt.
      onClose={() => {
        if (!saving) onClose(false);
      }}
      onSave={handleSave}
      maxWidth='sm'
      disableBackdropClosing
      dialogTitle={t('helpArticles:categories.orderTitle')}
      dialogSubTitle={t('helpArticles:categories.orderSubTitle', { category: categoryName })}
      dialogContent={
        // En liten snurra i listans ställe medan den laddas. Husets laddningsöverlägg är högre än en kort lista, så
        // dialogen fick en rullningslist som kom och gick.
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List dense disablePadding>
            {articles.map((article, index) => (
              <ListItem
                key={article.id}
                divider={index < articles.length - 1}
                // Plats för de två pilarna till höger.
                sx={{ pr: 10 }}
                secondaryAction={
                  // Ingen tooltip: den skymmer raden ovanför. Pilarna är självförklarande och har aria-label.
                  <Box>
                    <IconButton
                      size='small'
                      aria-label={t('helpArticles:categories.moveUp')}
                      disabled={index === 0 || saving || loading}
                      onClick={() => move(index, -1)}
                    >
                      <ArrowUpward fontSize='small' />
                    </IconButton>
                    <IconButton
                      size='small'
                      aria-label={t('helpArticles:categories.moveDown')}
                      disabled={index === articles.length - 1 || saving || loading}
                      onClick={() => move(index, 1)}
                    >
                      <ArrowDownward fontSize='small' />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemText primary={article.title} primaryTypographyProps={{ variant: 'body2' }} />
                {/* Utkast har en plats men syns inte för läsarna förrän de publiceras. */}
                {article.status === HelpPageStatusEnum.Draft && (
                  <Box sx={{ ml: 1, flexShrink: 0 }}>
                    <HelpPageStatusChip status={article.status} />
                  </Box>
                )}
              </ListItem>
            ))}
          </List>
        )
      }
      dialogActions={
        <>
          <Button variant='outlined' color='primary' disableElevation disabled={saving} onClick={() => onClose(false)}>
            {t('common:button.cancel')}
          </Button>
          <Button variant='contained' color='primary' disableElevation disabled={saving || loading || articles.length === 0} onClick={handleSave}>
            {t('common:button.save')}
          </Button>
        </>
      }
    />
  );
};
